import React, { useEffect, useMemo, useState } from 'react';
import { arrowRightIcon, arrowLeftIcon } from 'assets';
import { OpDataGrid } from 'op2mise-react-widgets';
import { Modal } from 'react-bootstrap';
import Button from 'shared/components/button/Button';
import { getMarketStations } from 'components/campaigns/requests';
import { saveOrApplyMarketStations } from 'components/campaigns/requests';
import ConfirmLeaveModal from 'shared/components/confirm-leave-modal/ConfirmLeaveModal';
import { useCallbackPrompt } from 'components/_reusable/blocker/UsePromptComponent.ts';
import { OpButton } from 'op2mise-react-widgets';
import AuthService from 'core/auth.service';
import { USER_TYPE } from 'enums/userRoles';
import BootstrapSpinner from 'shared/components/bootstrap-spinner/BootstrapSpinner';

const MarketGroupStationForm = (props) => {
  const {
    show,
    onHide,
    market,
    selectedCampaign,
    groupName,
    groupNameMarkets,
    setGroupNameMarkets,
    stationIds,
    setIsFetchingClientGroup,
  } = props;

  const userIdentity = AuthService.getUserIdentity();
  const isOrganisationAdmin = userIdentity.roleId === USER_TYPE.organisationadmin;

  // States
  const [availableStations, setAvailableStations] = useState([]);
  const [activeStations, setActiveStations] = useState([]);

  const [toSelectItems, setToSelectItems] = useState([]);
  const [toUnselectItems, setToUnselectItems] = useState([]);

  const [isFetchingStations, setIsFetchingStations] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isApplyLoading, setIsApplyLoading] = useState(false);
  const [isApplyAndSaveLoading, setIsApplyAndSaveLoading] = useState(false);

  // Dirty Forms
  const [isDirty, setIsDirty] = useState(false);
  const [showConfirmLeaveModal, setShowConfirmLeaveModal] = useState(false);
  const [showPrompt, confirmNavigation, cancelNavigation] = useCallbackPrompt(
    isDirty
  );

  // API Fetch
  const handleGetMarketStations = async () => {
    try {
      const payload = {
        marketId: market.marketId,
      };

      setIsFetchingStations(true);
      const res = await getMarketStations({
        campaignId: selectedCampaign?.id,
        data: JSON.stringify(payload),
      });

      setActiveStations(res?.activeStations);
      setAvailableStations(res?.availableStations);
      setIsFetchingStations(false);
    } catch (error) {
      console.error(error);
    }
  };

  // Datagrid Action
  const handleOnAddAllItems = () => {
    setActiveStations((prev) => [...prev, ...availableStations]);
    setAvailableStations([]);
    setIsDirty(true);
  };

  const handleOnAddSelected = () => {
    let selectedStation = toSelectItems.map((item) => item.data);
    let newActiveStations = [...selectedStation, ...activeStations];

    let newAvailableStations = availableStations.filter(
      (station) =>
        !selectedStation.some((selected) => selected.id === station.id)
    );

    setAvailableStations(newAvailableStations);
    setActiveStations(newActiveStations);

    setIsDirty(true);
  };

  const handleOnRemoveAll = () => {
    setAvailableStations((prev) => [...prev, ...activeStations]);
    setActiveStations([]);
    setIsDirty(true);
  };

  const handleOnRemoveSelected = () => {
    const selectedActiveStations = toUnselectItems.map((item) => item.data);

    let newActiveStations = activeStations.filter(
      (market) =>
        !selectedActiveStations.some((selected) => selected.id === market.id)
    );

    setActiveStations(newActiveStations);
    setAvailableStations((prev) => [...prev, ...selectedActiveStations]);

    setIsDirty(true);
  };

  const handleOnSaveAndProceed = () => {
    handleSaveActiveStations(false);
  };

  const handleOnDiscard = () => {
    confirmNavigation();
    onHide();
  };

  const handleOnHide = () => {
    if (showConfirmLeaveModal) setShowConfirmLeaveModal(false);
    else cancelNavigation();
  };

  const handleOnClose = (ignoreDirtyForm) => {
    if (isDirty && !ignoreDirtyForm) {
      setShowConfirmLeaveModal(true);
    } else {
      onHide();
    }
  };

  // Modal Actions
  const handleSaveActiveStations = async (isSave) => {
    if (!isSave) {
      setIsApplyLoading(true);
    } else {
      setIsApplyAndSaveLoading(true);
    }
    setIsSaving(true);
    setIsFetchingClientGroup(true);
    const stationId = [...activeStations].map((item) => parseInt(item.id, 10));
    const prevMarketStations = market.clientGroupMarketStations;

    const payload = {
      save: isSave,
      campaignId: selectedCampaign?.id,
      marketId: market?.marketId,
      stations: stationId,
      oldStations: prevMarketStations
        .filter((m) => m.saved)
        .map((item) => ({
          id: item.stationId,
          active: item.active,
          saved: item.saved,
        })),
    };

    const res = await saveOrApplyMarketStations({
      groupNameId: groupName?.id,
      data: JSON.stringify(payload),
    });

    const marketStations = await res?.clientGroupMarketStations;

    const newGroupMarkets = groupNameMarkets.map((m) => {
      if (m.marketId === market?.marketId) {
        return { ...m, clientGroupMarketStations: marketStations };
      }

      return {
        ...m,
        clientGroupMarketStations: m.clientGroupMarketStations.map((item) => {
          const matchingStation = stationIds
            .filter((st) => st.marketId === m.marketId)
            .find((s) => s.stationId === item.stationId);

          return matchingStation
            ? { ...item, active: matchingStation.active }
            : item;
        }),
      };
    });

    setGroupNameMarkets(newGroupMarkets);
    setIsSaving(false);
    setIsApplyLoading(false);
    setIsApplyAndSaveLoading(false);
    setIsDirty(false);
    setIsFetchingClientGroup(false);
    confirmNavigation();
    onHide();
  };

  const leftGridColumnDefs = useMemo(() => {
    return [
      {
        field: 'stationName',
        headerName: 'station/cable',
        sort: 'asc',
      },
      {
        field: 'alias',
        headerName: 'alias',
      },
    ];
  }, []);

  const rightGridColumnDefs = useMemo(() => {
    return [
      {
        field: 'stationName',
        headerName: 'station/cable',
        sort: 'asc',
      },
      {
        field: 'alias',
        headerName: 'alias',
      },
    ];
  }, []);

  useEffect(() => {
    handleGetMarketStations();
  }, []);

  return (
    <Modal
      centered
      onHide={() => handleOnClose(false)}
      show={show}
      dialogClassName="group-name-modal"
    >
      <div className="group-name-modal-wrapper">
        {isFetchingStations ? (
          <BootstrapSpinner />
        ) : (
          <>
            <p className="group-name-modal-title">{market?.name}</p>

            <div>
              <OpDataGrid
                actions={[
                  {
                    disabled: !availableStations.length,
                    icon: arrowLeftIcon,
                    iconPosition: 'right',
                    label: 'Add all',
                    onClick: handleOnAddAllItems,
                  },
                  {
                    disabled: !toSelectItems.length,
                    label: 'Add Selected',
                    icon: arrowLeftIcon,
                    iconPosition: 'right',
                    onClick: handleOnAddSelected,
                  },
                  {
                    disabled: !toUnselectItems.length,
                    icon: arrowRightIcon,
                    iconPosition: 'left',
                    label: 'Remove Selected',
                    onClick: handleOnRemoveSelected,
                  },
                  {
                    disabled: !activeStations.length,
                    icon: arrowRightIcon,
                    iconPosition: 'left',
                    label: 'Remove all',
                    onClick: handleOnRemoveAll,
                  },
                ]}
                alignedGrid={false}
                dualGridProps={{
                  leftGrid: {
                    columns: leftGridColumnDefs,
                    customVerbiage: 'No stations found',
                    defaultSort: {
                      field: 'name',
                      sort: 'asc',
                    },
                    enableMultipleRowSelection: true,
                    gridHeightBuffer: 600,
                    gridLabel: 'Available station/cables:',
                    onSelectionChanged: (params) => setToSelectItems(params),
                    resizable: true,
                    rows: availableStations,
                    sharedColumnProperties: {
                      filter: { fields: ['stationName', 'alias'], value: true },
                      flex: { fields: ['stationName', 'alias'], value: 1 },
                      sortable: { fields: ['stationName', 'alias'], value: true },
                    },
                    showAlternativeRowHighlight: true,
                    showRowSelectionCount: true,
                    suppressRowClickSelection: false,
                  },
                  rightGrid: {
                    columns: rightGridColumnDefs,
                    customVerbiage: 'Please select and add a station',
                    defaultSort: {
                      field: 'name',
                      sort: 'asc',
                    },
                    enableMultipleRowSelection: true,
                    gridLabel: 'Selected station/cables:',
                    resizable: true,
                    rows: activeStations,
                    sharedColumnProperties: {
                      filter: { fields: ['stationName', 'alias'], value: true },
                      flex: { fields: ['stationName', 'alias'], value: 1 },
                      sortable: { fields: ['stationName', 'alias'], value: true },
                    },
                    onSelectionChanged: setToUnselectItems,
                    showAlternativeRowHighlight: true,
                    showRowSelectionCount: true,
                    suppressRowClickSelection: false,
                  },
                }}
                mode={'DUAL'}
              />
            </div>

            <ConfirmLeaveModal
              show={showPrompt || showConfirmLeaveModal}
              onHide={handleOnHide}
              onSave={handleOnSaveAndProceed}
              onDiscard={handleOnDiscard}
            />

            <div className="align-items-center d-flex justify-content-end">
              <Button
                defaultBtn
                onClick={() => handleOnClose(false)}
                text="Cancel"
              />
              <OpButton
                onClick={() => handleSaveActiveStations(false)}
                label={'Apply'}
                disabled={isSaving}
                loading={isApplyLoading}
              />

              {isOrganisationAdmin && (
                <OpButton
                  label={'Apply and Save Market'}
                  style={{ marginLeft: '20px' }}
                  onClick={() => handleSaveActiveStations(true)}
                  disabled={isSaving}
                  loading={isApplyAndSaveLoading}
                />
              )}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default MarketGroupStationForm;
